import {
  Text,
  IconUserGroup,
  Icon3dBox,
  IconBank,
  IconBarChart,
  IconPieChart,
  CustomerLogo,
  Box
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { getLogoSrc } from 'Customer/utils/logo'
import { getTableSelectionLabel } from './helpers'

// Real API implementation
export const useChatContextData = (
  useMockData = false,
  revenueWaterfallData?: any,
  tableSelection?: any
) => {
  // Fetch real data from APIs
  const customersQuery = useQuery(
    dashboard20240730Client.getCustomers,
    {
      limit: 50 // Limit to top 50 customers for context
    },
    {
      enabled: !useMockData,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  )

  // Removed schedules from the context dropdown per product decision

  const productsQuery = useQuery(
    dashboardv99990101Client.getProducts,
    {
      limit: 50 // Limit to top 50 products for context
    },
    {
      enabled: !useMockData,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  )

  if (useMockData) {
    return mockContextOptions
  }

  // Transform real data into context options format
  const allCustomers = customersQuery.data?.items || []
  // const allBillingSchedules = billingSchedulesQuery.data?.items || []
  const allProducts = productsQuery.data?.items || []

  // Filter data to only include entities present in revenue waterfall
  let customers = allCustomers
  // const billingSchedules = allBillingSchedules
  const products = allProducts

  if (revenueWaterfallData?.data?.flatRows) {
    const waterfallRows = revenueWaterfallData.data.flatRows

    // Extract unique customer IDs from waterfall data
    const waterfallCustomerIds = new Set<string>()
    const waterfallProductIds = new Set<string>()
    const waterfallScheduleIds = new Set<string>()

    waterfallRows.forEach((row: any) => {
      // For customer-grouped rows, the customerId field contains the actual customer ID
      if (row.customerId && row.type === 'customer') {
        waterfallCustomerIds.add(row.customerId)
      }
      // For product-grouped rows, extract product information
      if (row.productId && row.type === 'product') {
        waterfallProductIds.add(row.productId)
      }
    })

    // Filter customers to only those present in waterfall
    if (waterfallCustomerIds.size > 0) {
      customers = allCustomers.filter(customer =>
        waterfallCustomerIds.has(customer.id)
      )
    }

    // For products and schedules, since the waterfall doesn't directly contain their IDs,
    // we'll show all available ones but could enhance this with better matching logic
    // based on the grouping type and labels in the future
  }

  const contextOptions = [
    {
      label: 'Customers',
      value: 'customers',
      searchValues: ['customers', 'clients'],
      icon: <IconUserGroup />,
      options: customers.slice(0, 10).map(customer => ({
        label: customer.legalName,
        value: `customer:${customer.id}`,
        searchValues: [
          customer.legalName.toLowerCase(),
          customer.id.toLowerCase()
        ],
        secondaryLabel: customer.contactName || customer.label || 'Customer',
        icon: <CustomerLogo src={getLogoSrc(customer)} />
      })),
      footerOptions: [
        {
          label: 'All customers',
          value: 'customer:all',
          searchValues: ['all', 'customers']
        },
        {
          label: 'Top 10 customers',
          value: 'customer:top10',
          searchValues: ['top', 'customers', 'highest']
        }
      ]
    },
    {
      label: 'Products',
      value: 'products',
      searchValues: ['products', 'services'],
      icon: <Icon3dBox />,
      options: products.slice(0, 10).map(product => ({
        label: product.name,
        value: `product:${product.id}`,
        searchValues: [product.name.toLowerCase(), product.id.toLowerCase()],
        secondaryLabel: product.label || 'Product',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#3B82F6"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              {product.name.charAt(0).toUpperCase()}
            </Text>
          </Box>
        )
      })),
      footerOptions: [
        {
          label: 'All products',
          value: 'product:all',
          searchValues: ['all', 'products']
        }
      ]
    },
    {
      label: 'Revenue recognition',
      value: 'revenue-recognition',
      searchValues: ['revenue', 'recognition', 'data'],
      icon: <IconBank />,
      options: [
        {
          label: 'Current month revenue',
          value: 'revenue:current-month',
          searchValues: ['current', 'month', 'revenue'],
          secondaryLabel: "This month's performance",
          icon: <IconBarChart size={12} color="#8B5CF6" />
        },
        {
          label: 'YTD revenue',
          value: 'revenue:ytd',
          searchValues: ['ytd', 'year', 'revenue'],
          secondaryLabel: 'Year to date totals',
          icon: <IconPieChart size={12} color="#8B5CF6" />
        },
        {
          label: 'Revenue waterfall',
          value: 'revenue:waterfall',
          searchValues: ['waterfall', 'current', 'view'],
          secondaryLabel: 'Current waterfall view',
          icon: <IconBank size={12} color="#8B5CF6" />
        }
      ],
      footerOptions: [
        {
          label: 'All revenue data',
          value: 'revenue:all',
          searchValues: ['all', 'revenue', 'data']
        }
      ]
    }
  ]

  // Add table selection options if available
  if (tableSelection) {
    const tableSelectionOption = {
      label: 'Table selection',
      value: 'table-selection',
      searchValues: ['table', 'selection', 'cells'],
      icon: (
        <Box
          w="12px"
          h="12px"
          bg="#F59E0B"
          borderRadius="2px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text fontSize="8px" color="white" fontWeight="bold">
            📋
          </Text>
        </Box>
      ),
      options: [
        {
          label: getTableSelectionLabel(tableSelection),
          value: `table-selection:current`,
          searchValues: ['table', 'selection', 'current'],
          secondaryLabel: `${tableSelection.rowCount} rows × ${tableSelection.columnCount} columns`,
          icon: (
            <Box
              w="12px"
              h="12px"
              bg="#F59E0B"
              borderRadius="2px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="8px" color="white" fontWeight="bold">
                📋
              </Text>
            </Box>
          )
        }
      ],
      footerOptions: []
    }

    contextOptions.push(tableSelectionOption)
  }

  return contextOptions
}

// Mock data for testing
const mockContextOptions = [
  {
    label: 'Customers',
    value: 'customers',
    searchValues: ['customers', 'clients'],
    icon: <IconUserGroup />,
    options: [
      {
        label: 'Vocode',
        value: 'customer:vocode',
        searchValues: ['vocode', 'voice'],
        secondaryLabel: '$96,316.10 revenue',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#F59E0B"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              V
            </Text>
          </Box>
        )
      },
      {
        label: 'Brex',
        value: 'customer:brex',
        searchValues: ['brex', 'fintech'],
        secondaryLabel: '$54,000.00 revenue',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#F59E0B"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              B
            </Text>
          </Box>
        )
      },
      {
        label: 'Ramp Ltd.',
        value: 'customer:ramp',
        searchValues: ['ramp', 'limited'],
        secondaryLabel: '$39,600.00 revenue',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#F59E0B"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              R
            </Text>
          </Box>
        )
      },
      {
        label: 'Resend Industries',
        value: 'customer:resend',
        searchValues: ['resend', 'industries'],
        secondaryLabel: '$33,750.00 revenue',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#F59E0B"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              R
            </Text>
          </Box>
        )
      }
    ],
    footerOptions: [
      {
        label: 'All Customers',
        value: 'customer:all',
        searchValues: ['all', 'customers']
      },
      {
        label: 'Top 10 Customers',
        value: 'customer:top10',
        searchValues: ['top', 'customers', 'highest']
      }
    ]
  },
  {
    label: 'Products',
    value: 'products',
    searchValues: ['products', 'services'],
    icon: <Icon3dBox />,
    options: [
      {
        label: 'PRD001 - SaaS Platform',
        value: 'product:prd001',
        searchValues: ['prd001', 'saas', 'platform'],
        secondaryLabel: 'Main platform product',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#3B82F6"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              P
            </Text>
          </Box>
        )
      },
      {
        label: 'PRD002 - Analytics Add-on',
        value: 'product:prd002',
        searchValues: ['prd002', 'analytics', 'addon'],
        secondaryLabel: 'Analytics module',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#3B82F6"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              P
            </Text>
          </Box>
        )
      },
      {
        label: 'PRD003 - Enterprise Suite',
        value: 'product:prd003',
        searchValues: ['prd003', 'enterprise', 'suite'],
        secondaryLabel: 'Enterprise features',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#3B82F6"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              P
            </Text>
          </Box>
        )
      }
    ],
    footerOptions: [
      {
        label: 'All Products',
        value: 'product:all',
        searchValues: ['all', 'products']
      }
    ]
  },
  {
    label: 'Revenue recognition',
    value: 'revenue-recognition',
    searchValues: ['revenue', 'recognition', 'data'],
    icon: <IconBank />,
    options: [
      {
        label: 'Current Month Revenue',
        value: 'revenue:current-month',
        searchValues: ['current', 'month', 'revenue'],
        secondaryLabel: "This month's performance",
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#8B5CF6"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              📊
            </Text>
          </Box>
        )
      },
      {
        label: 'YTD Revenue',
        value: 'revenue:ytd',
        searchValues: ['ytd', 'year', 'revenue'],
        secondaryLabel: 'Year to date totals',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#8B5CF6"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              📈
            </Text>
          </Box>
        )
      },
      {
        label: 'Revenue Waterfall',
        value: 'revenue:waterfall',
        searchValues: ['waterfall', 'current', 'view'],
        secondaryLabel: 'Current waterfall view',
        icon: (
          <Box
            w="12px"
            h="12px"
            bg="#8B5CF6"
            borderRadius="2px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="8px" color="white" fontWeight="bold">
              💧
            </Text>
          </Box>
        )
      }
    ],
    footerOptions: [
      {
        label: 'All Revenue Data',
        value: 'revenue:all',
        searchValues: ['all', 'revenue', 'data']
      }
    ]
  }
]
